/**
 * Shared, mutable state of the fiber field ("Le Fil").
 *
 * Written by the scroll choreography (GSAP tweens in src/lib/choreography.ts)
 * and by a window pointer listener; read on every frame by the WebGL loop in
 * src/three/FiberCanvas.tsx. It changes on every scroll frame, so it must never
 * live in React state.
 */
export type FiberState = {
	/** 0 = bundle (hero), 1 = weave (manifesto), 2 = meadow (vision). Fractional values blend. */
	morph: number;
	/** Global visibility of the whole field, 0..1. */
	opacity: number;
	/** 0 = day palette (light page), 1 = dusk palette (Vision chapter on Forest Night). */
	theme: number;
	/** Page colour scheme, 0 = light, 1 = dark (<html data-theme>), kept in sync by src/three/FiberField.ts. */
	dark: number;
	/** 0..1, how far the hero has been scrolled away (lets the bundle drift and thin out). */
	heroProgress: number;
	/** Pointer in normalized device coordinates (-1..1, y up). */
	pointer: { x: number; y: number; active: boolean };
};

/** 1 when <html data-theme="dark">, else 0. */
export function readPageTheme(): number {
	if (typeof document === 'undefined') return 0;
	return document.documentElement.dataset.theme === 'dark' ? 1 : 0;
}

const initial = (): FiberState => ({
	morph: 0,
	opacity: 1,
	theme: 0,
	dark: readPageTheme(),
	heroProgress: 0,
	pointer: { x: 0, y: 0, active: false },
});

export const fiberState: FiberState = initial();

/** Restore the initial values (called when the home page unmounts). */
export function resetFiberState(): void {
	Object.assign(fiberState, initial());
}
